import {z} from 'zod/v4';
import {
  SourceFile,
  sourceFileConstraints,
  SourceFileGeneration,
  SourceFileOrigin,
  UpsertSourceFile,
} from '@packages/domains/code-libraries/data-types';
import {instructionConstraints} from './constants';
import {
  CmsInstructionGenerationSubject,
  SiteInstructionGenerationSubject,
} from './generation/subject';
import {
  ManagedInstructionOwner,
  ManagedInstructions,
  type ManagedInstructionSite,
} from './managed-instructions';
import {
  isAgentInstructionFilePath,
  isAgentInstructionSubfolderPath,
  isAgentRulePath,
  isAgentRuleSubfolderPath,
  isAgentSkillPath,
  isAgentSkillSubfolderPath,
  isAgentSkillSubtreeFilePath,
  isGeneratedReferenceFilePath,
} from './utils';

/**
 * Why an instruction cannot be edited in the Agent Instructions panel. The
 * reason is surfaced so the panel can name the editor that does own the
 * document instead of leaving the customer at a disabled field.
 */
export type InstructionReadOnlyReason =
  | 'library-owned'
  | 'generated-reference'
  | 'managed';

export function getInstructionReadOnlyReason(
  instruction:
    | Pick<AgentInstruction, 'origin' | 'path' | 'managedBy'>
    | undefined,
  site: ManagedInstructionSite
): InstructionReadOnlyReason | undefined {
  if (instruction?.origin !== undefined) return 'library-owned';
  if (ManagedInstructions.owner(instruction, site)) return 'managed';
  if (instruction?.path === undefined) return undefined;
  if (isGeneratedReferenceFilePath(instruction.path)) {
    return 'generated-reference';
  }

  return undefined;
}

export function isInstructionReadOnly(
  instruction:
    | Pick<AgentInstruction, 'origin' | 'path' | 'managedBy'>
    | undefined,
  site: ManagedInstructionSite
): boolean {
  return getInstructionReadOnlyReason(instruction, site) !== undefined;
}

// Upsert is the file-write surface; subfolder paths have their own
// `createFolder` endpoint. Without this refinement, folder-shaped paths pass
// schema validation and reach the service's dispatcher, where the only guard
// is a plain `Error('Cannot upsert a folder path.')` that the Nest controller
// renders as a generic 500 instead of an actionable 4xx.
const isAgentInstructionUpsertablePath = (path: string): boolean =>
  !isAgentInstructionSubfolderPath(path);

export type AgentInstructionFilePath = z.infer<typeof AgentInstructionFilePath>;
export const AgentInstructionFilePath = z
  .string()
  .min(1)
  .max(instructionConstraints.filePath.maxLength)
  .refine(isAgentInstructionFilePath, {
    message: 'Agent instruction file paths must be safe relative paths.',
  });

export type AgentInstructionContent = z.infer<typeof AgentInstructionContent>;
export const AgentInstructionContent = z
  .string()
  .max(instructionConstraints.content.maxLength, {
    message: 'Agent instruction content must be text content.',
  })
  .transform((source) =>
    // eslint-disable-next-line no-control-regex
    source.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ')
  );

export const AgentInstructionKind = z.enum(['skill', 'rule']);
export type AgentInstructionKind = z.infer<typeof AgentInstructionKind>;

export type InstructionGeneration = z.infer<typeof InstructionGeneration>;
export const InstructionGeneration = z.discriminatedUnion('type', [
  CmsInstructionGenerationSubject.extend({
    taskId: SourceFileGeneration.shape.taskId,
  }),
  SiteInstructionGenerationSubject.extend({
    taskId: SourceFileGeneration.shape.taskId,
  }),
]);

export type AgentInstructionFile = z.infer<typeof AgentInstructionFile>;
export const AgentInstructionFile = SourceFile.omit({
  folderId: true,
  name: true,
  content: true,
  origin: true,
  generation: true,
}).extend({
  kind: AgentInstructionKind,
  path: AgentInstructionFilePath,
  content: AgentInstructionContent,
  origin: SourceFileOrigin.optional(),
  generation: InstructionGeneration.optional(),
  // Additive alongside `path`, for clients that need the raw identity.
  folderId: SourceFile.shape.folderId.optional(),
  name: SourceFile.shape.name.optional(),
  managedBy: ManagedInstructionOwner.optional(),
});

export const isAgentInstructionPath = (path: string): boolean => {
  return (
    isAgentInstructionFilePath(path) &&
    (isAgentRulePath(path) ||
      isAgentSkillPath(path) ||
      isAgentSkillSubtreeFilePath(path) ||
      isAgentSkillSubfolderPath(path) ||
      isAgentRuleSubfolderPath(path))
  );
};

export type AgentInstructionPath = z.infer<typeof AgentInstructionPath>;
export const AgentInstructionPath = AgentInstructionFilePath.refine(
  isAgentInstructionPath,
  {
    message:
      'Agent instructions must use relative rules/*.md, rules/*.mdc, rules/subfolder, skill-name/SKILL.md, skill-name/subfolder/file.md, or skill-name/subfolder paths.',
  }
);

export type AgentInstruction = z.infer<typeof AgentInstruction>;
export const AgentInstruction = AgentInstructionFile.omit({
  path: true,
  content: true,
}).extend({
  path: AgentInstructionPath,
  markdown: AgentInstructionContent,
  // Optional for two reasons: (1) rollout — servers that haven't deployed
  // kind population yet omit it; (2) system instructions from the registry
  // are built without it (see instructions/registry transformations.ts).
  // Rules carry 'rule'; skill files and their companions carry 'skill'
  // (mirroring SourceFile.kind in storage).
  kind: AgentInstructionKind.optional(),
});

export type AgentInstructionListItem = z.infer<typeof AgentInstructionListItem>;
export const AgentInstructionListItem = AgentInstruction.omit({
  markdown: true,
});

export type SearchOptions = z.infer<typeof SearchOptions>;
export const SearchOptions = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(instructionConstraints.list.query.max)
    .optional(),
  description: z
    .string()
    .trim()
    .min(1)
    .max(instructionConstraints.list.query.max)
    .optional(),
  markdown: z
    .string()
    .trim()
    .min(1)
    .max(instructionConstraints.list.query.max)
    .optional(),
  limit: z
    .number()
    .int()
    .min(instructionConstraints.list.limit.min)
    .max(instructionConstraints.list.limit.max)
    .default(instructionConstraints.list.limit.default),
  offset: z
    .number()
    .int()
    .min(instructionConstraints.list.offset.min)
    .default(instructionConstraints.list.offset.default),
});

export type AgentInstructionPagination = z.infer<
  typeof AgentInstructionPagination
>;
export const AgentInstructionPagination = z.object({
  limit: z
    .number()
    .int()
    .min(instructionConstraints.list.limit.min)
    .max(instructionConstraints.list.limit.max),
  offset: z.number().int().min(instructionConstraints.list.offset.min),
  total: z.number().int().min(0),
});

export type AgentInstructionList = z.infer<typeof AgentInstructionList>;
export const AgentInstructionList = z.object({
  instructions: z.array(AgentInstructionListItem),
  pagination: AgentInstructionPagination,
});

export type AgentInstructionLibraryMetadata = z.infer<
  typeof AgentInstructionLibraryMetadata
>;
export const AgentInstructionLibraryMetadata = z.object({
  sourceFilesCount: z.number().int().min(0),
  /** Counts only skill root files (`<skill-name>/SKILL.md`), not subtree files. */
  skillsCount: z.number().int().min(0),
  rulesCount: z.number().int().min(0),
});

export type ReadAgentInstructionPathOnly = z.infer<
  typeof ReadAgentInstructionPathOnly
>;
export const ReadAgentInstructionPathOnly = z.object({
  path: AgentInstructionPath,
});

const QueryStringBoolean = z.preprocess((value) => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return value;
}, z.boolean());

export type ReadAgentInstruction = z.infer<typeof ReadAgentInstruction>;
export const ReadAgentInstruction = ReadAgentInstructionPathOnly.extend({
  resolveReferences: z.boolean().default(true),
});

export type ReadAgentInstructionQuery = z.infer<
  typeof ReadAgentInstructionQuery
>;
export const ReadAgentInstructionQuery = ReadAgentInstructionPathOnly.extend({
  resolveReferences: QueryStringBoolean.default(false),
});

export type UpsertAgentInstruction = z.infer<typeof UpsertAgentInstruction>;
export const UpsertAgentInstruction = UpsertSourceFile.pick({
  siteId: true,
  description: true,
  isDraft: true,
}).extend({
  path: AgentInstructionPath.refine(isAgentInstructionUpsertablePath, {
    message:
      'Upsert requires a file path. Use the folder create endpoint for subfolders.',
  }),
  // Optional so metadata-only writes (description / isDraft) can be issued
  // without re-sending the markdown body — used by the metadata panel's
  // commit-on-blur flow (AIO-260, Option A). When absent, the service leaves
  // the persisted content untouched.
  markdown: AgentInstructionContent.optional(),
  version: z.number().int().min(sourceFileConstraints.version.min),
});

export type MoveAgentInstruction = z.infer<typeof MoveAgentInstruction>;
export const MoveAgentInstruction = UpsertSourceFile.pick({
  siteId: true,
}).extend({
  fromPath: AgentInstructionPath,
  toPath: AgentInstructionPath,
  version: z.number().int().min(sourceFileConstraints.version.min),
});

export type MoveAgentInstructionResponse = z.infer<
  typeof MoveAgentInstructionResponse
>;
export const MoveAgentInstructionResponse = z.object({
  fromPath: AgentInstructionPath,
  toPath: AgentInstructionPath,
  instruction: AgentInstruction.optional(),
});

// AIO-285 slice 2: explicit endpoint for materializing empty subfolders.
// Empty folders are a first-class state — they survive reloads even without
// contents, matching the rules section's existing BE shape. File-shaped
// paths and bare section roots are rejected; the refinement narrows the
// already-validated `AgentInstructionPath` to subfolder shapes only.
export type CreateInstructionFolder = z.infer<typeof CreateInstructionFolder>;
export const CreateInstructionFolder = UpsertSourceFile.pick({
  siteId: true,
}).extend({
  path: AgentInstructionPath.refine(isAgentInstructionSubfolderPath, {
    message:
      'Folder create requires a skill or rule subfolder path (e.g. brand/references or rules/security).',
  }),
});

export type CreateInstructionFolderResult = z.infer<
  typeof CreateInstructionFolderResult
>;
export const CreateInstructionFolderResult = z.object({
  kind: z.literal('folder'),
  path: AgentInstructionPath,
});

// AIO-285 slice 4: the read counterpart to CreateInstructionFolder. Surfaces
// every subfolder under skill roots and the rules root so the Designer tree
// can render empty folders that have no file descendants. File-derived folder
// nodes still drive the tree on the FE — these rows are purely additive for
// folders that the file listing wouldn't otherwise discover.
export type AgentInstructionFolder = z.infer<typeof AgentInstructionFolder>;
export const AgentInstructionFolder = z.object({
  path: AgentInstructionPath.refine(isAgentInstructionSubfolderPath, {
    message: 'Folder paths must be a skill or rule subfolder.',
  }),
});

export type AgentInstructionFolderList = z.infer<
  typeof AgentInstructionFolderList
>;
export const AgentInstructionFolderList = z.object({
  folders: z.array(AgentInstructionFolder),
});

/**
 * Snapshot of every agent instruction available for a site, shaped for
 * the AI Assistant system prompt. `alwaysApplied` rules carry full
 * markdown so the prompt can render them verbatim; `available` carries
 * metadata only — the agent reads full content on demand via the
 * `read-instruction` tool.
 */
export type AgentInstructionPromptContext = z.infer<
  typeof AgentInstructionPromptContext
>;
export const AvailableAgentInstructions = z.object({
  skills: z.array(AgentInstructionListItem),
  rules: z.array(AgentInstructionListItem),
});
export type AvailableAgentInstructions = z.infer<
  typeof AvailableAgentInstructions
>;

export const AgentInstructionPromptContext = z.object({
  alwaysApplied: z.array(AgentInstruction),
  available: AvailableAgentInstructions,
});

export type AgentInstructionScope = 'readonly' | 'manage';
